"use client";

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
  useContext,
  useRef,
} from "react";
import { useSession } from "next-auth/react";
import { useCartStore } from "@/store/cart-store";
import {
  Cart,
  CartItem as BaseCartItem,
  UserSession,
  CartItemWithGroup,
} from "@/types";
import { generateCartItemId } from "@/lib/utils";
import { pusherClient } from "@/lib/pusher";

import { useToast } from "@/hooks/use-toast";

// Extend CartItem to include addedAt for local use
interface CartItem extends BaseCartItem {
  addedAt?: number;
}

// Utility function to remove circular references from cart items
function prepareCartForStorage(cart: Cart): Cart {
  if (!cart) return cart;

  try {
    // Generate a valid UUID for the cart if it doesn't have one or isn't valid
    const generateValidUUID = () => {
      return crypto.randomUUID();
    };

    // Check if a string is a valid UUID
    const isValidUUID = (str: string) => {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(str);
    };

    // Ensure valid cart ID
    const cartId =
      cart.id && isValidUUID(cart.id) ? cart.id : generateValidUUID();

    // Create a deep copy of the cart without circular references
    const safeCart: Cart = {
      ...cart,
      id: cartId,
      // Ensure dates are proper Date objects (they might be strings from localStorage)
      created:
        cart.created instanceof Date
          ? cart.created
          : new Date(cart.created || Date.now()),
      updated:
        cart.updated instanceof Date
          ? cart.updated
          : new Date(cart.updated || Date.now()),
      // Ensure required fields have proper defaults
      name: cart.name || cart.quote_number || "Cart",
      notes: cart.notes || "",
      discarded: Boolean(cart.discarded),
      printed: Boolean(cart.printed),
      purchased: Boolean(cart.purchased),
      // Set needsMigration for local carts with items
      needsMigration: cart.isServerCart
        ? cart.needsMigration !== undefined
          ? cart.needsMigration
          : false // Preserve existing needsMigration for server carts, default to false if undefined
        : cart.cart_items && cart.cart_items.length > 0, // Local carts with items need migration
      // Ensure user_data meets UserSessionSchema requirements
      user_data: {
        id: cart.user_data?.id || null,
        role: cart.user_data?.role || "user",
        jobTitle: cart.user_data?.jobTitle || null,
        email: cart.user_data?.email || null,
        firstName: cart.user_data?.firstName || null,
        lastName: cart.user_data?.lastName || null,
        emailVerified: cart.user_data?.emailVerified || null,
        phone: cart.user_data?.phone || null,
        image: cart.user_data?.image || null,
        companyId: cart.user_data?.companyId || null,
        companyName: cart.user_data?.companyName || null,
        companyImage: cart.user_data?.companyImage || null,
        paymentTerms: cart.user_data?.paymentTerms || "cash_on_delivery",
        stripeCustomerId: cart.user_data?.stripeCustomerId || null,
        tierId: cart.user_data?.tierId || null,
        addressId: cart.user_data?.addressId || null,
        permissions: cart.user_data?.permissions || {},
      },
      // Clean cart items
      cart_items: cart.cart_items
        ? cart.cart_items.map((item) => {
            // Create a new item without _group and _quoteNumber
            const itemWithGroup = item as CartItemWithGroup;

            const safeItem: CartItem = {
              item_id: itemWithGroup.item_id,
              // Ensure UUIDs are valid
              product_id:
                itemWithGroup.product_id &&
                isValidUUID(itemWithGroup.product_id as string)
                  ? (itemWithGroup.product_id as string)
                  : generateValidUUID(),
              tier_id:
                itemWithGroup.tier_id &&
                isValidUUID(itemWithGroup.tier_id as string)
                  ? (itemWithGroup.tier_id as string)
                  : "460a3d62-c14a-4b55-8045-7ec079527d47", // Default tier ID
              // Ensure numeric fields are valid
              price: Math.max(0.01, Number(itemWithGroup.price) || 0.01),
              title: itemWithGroup.title || "Product",
              part_number: itemWithGroup.part_number || "UNKNOWN",
              image: itemWithGroup.image,
              weight: Number(itemWithGroup.weight) || 0,
              moq: Math.max(1, Math.floor(Number(itemWithGroup.moq)) || 1),
              shipping_weight: Number(itemWithGroup.shipping_weight) || 0,
              // Handle optional numeric fields
              width: itemWithGroup.width ? Number(itemWithGroup.width) : null,
              height: itemWithGroup.height
                ? Number(itemWithGroup.height)
                : null,
              length: itemWithGroup.length
                ? Number(itemWithGroup.length)
                : null,
              shipping_width: itemWithGroup.shipping_width
                ? Number(itemWithGroup.shipping_width)
                : null,
              shipping_height: itemWithGroup.shipping_height
                ? Number(itemWithGroup.shipping_height)
                : null,
              shipping_length: itemWithGroup.shipping_length
                ? Number(itemWithGroup.shipping_length)
                : null,
              decal_number: itemWithGroup.decal_number,
              quantity: itemWithGroup.quantity,
            };

            // Handle attachments safely by also removing circular references
            if (
              itemWithGroup.attachments &&
              Array.isArray(itemWithGroup.attachments)
            ) {
              safeItem.attachments = itemWithGroup.attachments.map((att) => {
                const attWithGroup = att as CartItemWithGroup;
                return {
                  item_id: attWithGroup.item_id,
                  product_id:
                    attWithGroup.product_id &&
                    isValidUUID(attWithGroup.product_id as string)
                      ? (attWithGroup.product_id as string)
                      : generateValidUUID(),
                  part_number: attWithGroup.part_number || "UNKNOWN",
                  decal_number: attWithGroup.decal_number,
                  price: Math.max(0.01, Number(attWithGroup.price) || 0.01),
                  title: attWithGroup.title || "Attachment",
                  image: attWithGroup.image,
                  tier_id:
                    attWithGroup.tier_id &&
                    isValidUUID(attWithGroup.tier_id as string)
                      ? (attWithGroup.tier_id as string)
                      : "460a3d62-c14a-4b55-8045-7ec079527d47",
                  quantity: Math.max(
                    1,
                    Math.floor(Number(attWithGroup.quantity)) || 1
                  ),
                  shipping_weight: Number(attWithGroup.shipping_weight) || 0,
                  shipping_width: attWithGroup.shipping_width
                    ? Number(attWithGroup.shipping_width)
                    : null,
                  shipping_height: attWithGroup.shipping_height
                    ? Number(attWithGroup.shipping_height)
                    : null,
                  shipping_length: attWithGroup.shipping_length
                    ? Number(attWithGroup.shipping_length)
                    : null,
                  weight: Number(attWithGroup.weight) || 0,
                  width: attWithGroup.width ? Number(attWithGroup.width) : null,
                  height: attWithGroup.height
                    ? Number(attWithGroup.height)
                    : null,
                  length: attWithGroup.length
                    ? Number(attWithGroup.length)
                    : null,
                  moq: Math.max(1, Math.floor(Number(attWithGroup.moq)) || 1),
                };
              });
            }

            return safeItem;
          })
        : [],
    };

    // Remove any snake_case fields that might have been added from the database
    // This prevents duplication of needs_migration vs needsMigration
    if ("needs_migration" in safeCart) {
      delete (safeCart as Cart & { needs_migration?: boolean }).needs_migration;
    }

    // Verify it's serializable
    try {
      JSON.stringify(safeCart);
      return safeCart;
    } catch (error) {
      console.error(
        "[CartProvider] Safe cart still has serialization issues:",
        error
      );
      // Fall back to an even safer approach if there are still issues
      return {
        ...cart,
        id: generateValidUUID(),
        created: new Date(),
        updated: new Date(),
        name: cart.name || "Cart",
        notes: "",
        discarded: false,
        printed: false,
        purchased: false,
        needsMigration: false,
        cart_items: [], // Empty cart items to be safe
      };
    }
  } catch (e) {
    console.error("[CartProvider] Error in prepareCartForStorage:", e);
    // Return a minimal safe version
    return {
      ...cart,
      id: crypto.randomUUID(),
      created: new Date(),
      updated: new Date(),
      name: cart.name || "Cart",
      notes: "",
      discarded: false,
      printed: false,
      purchased: false,
      needsMigration: false,
      cart_items: [],
    };
  }
}

// Utility function to clean cart items for server actions (removes circular references)
function cleanCartItemsForServer(cartItems: CartItem[]): CartItem[] {
  if (!cartItems || !Array.isArray(cartItems)) return [];

  return cartItems.map((item) => {
    // Remove any circular reference properties
    const cleanItem: CartItem = {
      item_id: item.item_id,
      product_id: item.product_id,
      tier_id: item.tier_id,
      price: item.price,
      title: item.title,
      part_number: item.part_number,
      image: item.image,
      weight: item.weight || 0,
      width: item.width,
      height: item.height,
      length: item.length,
      moq: item.moq || 1,
      shipping_weight: item.shipping_weight || 0,
      shipping_width: item.shipping_width,
      shipping_height: item.shipping_height,
      shipping_length: item.shipping_length,
      decal_number: item.decal_number,
    };

    // Clean attachments if they exist
    if (item.attachments && Array.isArray(item.attachments)) {
      cleanItem.attachments = item.attachments.map((attachment) => ({
        item_id: attachment.item_id,
        product_id: attachment.product_id,
        tier_id: attachment.tier_id,
        quantity: attachment.quantity || 1,
        price: attachment.price || 0,
        title: attachment.title || "",
        part_number: attachment.part_number || "",
        image: attachment.image,
        weight: attachment.weight || 0,
        width: attachment.width,
        height: attachment.height,
        length: attachment.length,
        moq: attachment.moq || 1,
        shipping_weight: attachment.shipping_weight || 0,
        shipping_width: attachment.shipping_width,
        shipping_height: attachment.shipping_height,
        shipping_length: attachment.shipping_length,
        decal_number: attachment.decal_number,
      }));
    }

    return cleanItem;
  });
}

// Export the ServerActions interface
export interface ServerActions {
  getUserCarts: () => Promise<Cart[]>;
  getCart: (quoteNumber: string) => Promise<Cart>;
  createCart: (
    name: string,
    cartItems?: CartItem[],
    printed?: boolean
  ) => Promise<Cart>;
  closeCart: (quoteNumber: string) => Promise<void>;
  bulkCartOperation: (
    quoteNumber: string,
    operations: { item: CartItem; quantity: number }[]
  ) => Promise<{
    success: boolean;
    itemsAdded: number;
    itemsRemoved: number;
  }>;
  updateCartName: (quoteNumber: string, name: string) => Promise<Cart>;
  updateCartPrices: (quoteNumber: string) => Promise<{
    updatedItems: CartItem[];
    pricesChanged: boolean;
  }>;
  updateCartUserData: (
    quoteNumber: string,
    userData: UserSession
  ) => Promise<{
    success: boolean;
    data: { success: boolean };
  }>;
  getMostRecentCart: () => Promise<Cart | null>;
  clearCart: (quoteNumber: string) => Promise<void>;
  migrateGuestCartsToUser: (guestCarts: Cart[]) => Promise<{
    success: boolean;
    convertedCarts?: number;
    updatedCarts?: number;
    results?: { success: boolean; cart?: Cart; error?: string }[];
    error?: string;
  }>;
}

// Define context type
interface CartContextType {
  carts: Cart[];
  activeQuoteNumber: string | null;
  isLoading: boolean;
  isDataSourceReady: boolean;
  hasAvailableCarts: boolean;

  // Cart operations
  setActiveCart: (quoteNumber: string) => void;
  addCart: (name?: string) => Promise<string>;
  closeCartTab: (quoteNumber: string) => Promise<void>;
  renameCart: (quoteNumber: string, name: string) => Promise<boolean>;
  clearCart: (quoteNumber: string) => Promise<boolean>;
  deleteCartTab: (quoteNumber: string) => Promise<boolean>;
  getCart: (
    quoteNumber: string
  ) => Promise<{ success: boolean; cart?: Cart; error?: string }>;
  bulkCartOperation: (
    quoteNumber: string,
    operations: { item: CartItem; quantity: number }[]
  ) => Promise<{ success: boolean; itemsAdded: number; itemsRemoved: number }>;
  convertGuestCartToServer: (quoteNumber: string) => Promise<Cart | null>;
  updateCartsOrder: (quoteNumbers: string[]) => void;
  replaceCart: (oldQuoteNumber: string, newCart: Cart) => void;
}

// Create the context
const CartContext = createContext<CartContextType | null>(null);

// Provider props interface
interface CartProviderProps {
  children: ReactNode;
  serverActions: ServerActions;
}

export const CartProvider: React.FC<CartProviderProps> = ({
  children,
  serverActions,
}) => {
  const { toast } = useToast();
  const { data: session, status } = useSession();
  const [isLoading, setIsLoading] = useState(true);
  const [isDataSourceReady, setIsDataSourceReady] = useState(false);
  const [serverCarts, setServerCarts] = useState<Cart[]>([]);

  const [migrationCompleted, setMigrationCompleted] = useState(false);
  const [migrationInProgress, setMigrationInProgress] = useState(false);
  const [lastSessionStatus, setLastSessionStatus] = useState<string | null>(
    null
  );

  // Add a ref to track if migration has been initiated
  const migrationInitiatedRef = useRef(false);
  // Add a ref to track if cart initialization has been initiated
  const cartInitializationRef = useRef(false);
  // Add a ref to track carts currently being converted
  const cartsBeingConvertedRef = useRef(new Set());
  // Add a ref to track login processing to prevent race conditions
  const loginProcessingRef = useRef(false);

  // Add refs to track visibility state and separate timing for sync vs monitoring
  const visibilityStateRef = useRef<string>(
    typeof document !== "undefined" ? document.visibilityState : "visible"
  );
  const lastSyncTimeRef = useRef<number>(Date.now());

  // Track conversion attempts in session storage to prevent duplicates across remounts
  const getConversionTrackingKey = useCallback(
    (cartQuoteNumber: string) => `cart-conversion-${cartQuoteNumber}`,
    []
  );

  // Global creation lock to prevent concurrent cart creations
  const getCreationLockKey = useCallback(
    () => `cart-creation-lock-${session?.user?.id || "guest"}`,
    [session?.user?.id]
  );

  const acquireCreationLock = useCallback((): boolean => {
    if (typeof window === "undefined") return true;

    const lockKey = getCreationLockKey();
    const now = Date.now();
    const existingLock = sessionStorage.getItem(lockKey);

    if (existingLock) {
      const lockTime = parseInt(existingLock, 10);
      // If lock is less than 5 seconds old, deny access
      if (now - lockTime < 5000) {
        return false;
      }
    }

    // Acquire the lock
    sessionStorage.setItem(lockKey, now.toString());
    return true;
  }, [getCreationLockKey]);

  const releaseCreationLock = useCallback((): void => {
    if (typeof window === "undefined") return;

    const lockKey = getCreationLockKey();
    sessionStorage.removeItem(lockKey);
  }, [getCreationLockKey]);

  const isCartBeingConverted = useCallback(
    (cartQuoteNumber: string): boolean => {
      // Check in-memory tracking first
      if (cartsBeingConvertedRef.current.has(cartQuoteNumber)) {
        return true;
      }

      // Check session storage for recent conversion attempts
      if (typeof window !== "undefined") {
        const trackingKey = getConversionTrackingKey(cartQuoteNumber);
        const conversionTime = sessionStorage.getItem(trackingKey);

        if (conversionTime) {
          const timeSinceConversion = Date.now() - parseInt(conversionTime, 10);
          // Reduce timeout to 10 seconds instead of 30 for faster recovery
          if (timeSinceConversion < 10000) {
            return true;
          } else {
            sessionStorage.removeItem(trackingKey);
          }
        }
      }

      return false;
    },
    [getConversionTrackingKey]
  );

  const markCartAsBeingConverted = useCallback(
    (cartQuoteNumber: string): void => {
      // Mark in memory
      cartsBeingConvertedRef.current.add(cartQuoteNumber);

      // Mark in session storage
      if (typeof window !== "undefined") {
        const trackingKey = getConversionTrackingKey(cartQuoteNumber);
        sessionStorage.setItem(trackingKey, Date.now().toString());
      }
    },
    [getConversionTrackingKey]
  );

  const clearConversionTracking = useCallback(
    (cartQuoteNumber: string): void => {
      // Clear from memory
      cartsBeingConvertedRef.current.delete(cartQuoteNumber);

      // Clear from session storage
      if (typeof window !== "undefined") {
        const trackingKey = getConversionTrackingKey(cartQuoteNumber);
        sessionStorage.removeItem(trackingKey);
      }
    },
    [getConversionTrackingKey]
  );

  // Access Zustand store
  const {
    carts: localCarts,
    activeQuoteNumber,
    initialized,
    addCart: addLocalCart,
    removeCart: removeLocalCart,
    setActiveCart: setLocalActiveCart,
    updateCart: updateLocalCart,
    updateCartsOrder: updateLocalCartsOrder,
    replaceCart: replaceLocalCart,
  } = useCartStore();

  // Helper function to determine if a cart is both printed and older than 30 days (expired)
  const isExpiredPrinted = useCallback((cart: Cart): boolean => {
    if (!cart?.printed) return false;

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const createdDate =
      cart.created instanceof Date ? cart.created : new Date(cart.created);
    return createdDate < thirtyDaysAgo;
  }, []);

  // Combine server and local carts, ensuring no duplicates and preserving order
  const carts = useMemo(() => {
    // Start with the order from localCarts (which maintains tab order)
    const combinedCarts: Cart[] = [];

    // First, add all local carts, but replace with server version if it exists
    localCarts.forEach((localCart) => {
      // Check if we have a server version of this cart
      const serverVersion = serverCarts.find(
        (serverCart) => serverCart.quote_number === localCart.quote_number
      );

      if (serverVersion) {
        // Use the server version but maintain the position from localCarts
        combinedCarts.push(serverVersion);
      } else {
        // Use the local cart
        combinedCarts.push(localCart);
      }
    });

    // Then add any server carts that don't exist in localCarts (shouldn't happen in normal flow)
    serverCarts.forEach((serverCart) => {
      const existsInLocal = localCarts.some(
        (localCart) => localCart.quote_number === serverCart.quote_number
      );

      if (!existsInLocal) {
        combinedCarts.push(serverCart);
      }
    });

    // Filter out carts that are printed and older than 30 days – these are treated as "archived" quotes
    const filteredCarts = combinedCarts.filter(
      (cart) => !isExpiredPrinted(cart)
    );

    return filteredCarts;
  }, [serverCarts, localCarts, isExpiredPrinted]);

  // Handle setting active cart
  const setActiveCart = useCallback(
    (quoteNumber: string) => {
      const cart = carts.find((c) => c.quote_number === quoteNumber);

      // Prevent setting expired printed carts as active
      if (!cart || isExpiredPrinted(cart)) {
        return;
      }

      setLocalActiveCart(quoteNumber);

      // Also update active-cart-data in localStorage
      try {
        localStorage.setItem(
          "active-cart-data",
          JSON.stringify(prepareCartForStorage(cart))
        );
      } catch (error) {
        console.error(
          "[CartProvider] Failed to update active-cart-data:",
          error
        );
      }
    },
    [carts, setLocalActiveCart, isExpiredPrinted]
  );

  // Add a new cart
  const addCart = useCallback(
    async (name?: string): Promise<string> => {
      setIsLoading(true);
      try {
        if (session?.user) {
          // Use server action to create cart
          const newCart = await serverActions.createCart(name || "Cart");

          if (newCart) {
            const serverCart = {
              ...newCart,
              isServerCart: true,
            };

            // Add to server carts
            setServerCarts((prev) => {
              return [...prev, serverCart];
            });

            // Also add to Zustand store to ensure it's immediately available
            addLocalCart(serverCart);

            // Set as active cart
            setActiveCart(serverCart.quote_number);

            return serverCart.quote_number;
          } else {
            throw new Error("Failed to create server cart");
          }
        } else {
          // Create local cart for guest users
          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substring(2, 9);
          const quoteNumber = `QT-GUEST-${timestamp}-${randomId}`;

          const newCart: Cart = {
            id: `local-${timestamp}`,
            quote_number: quoteNumber,
            name: name || "Cart",
            user_data: {
              role: "user" as const,
              id: null,
              email: null,
              firstName: null,
              lastName: null,
              emailVerified: null,
              jobTitle: null,
              phone: null,
              image: null,
              companyId: null,
              companyName: null,
              companyImage: null,
              paymentTerms: "cash_on_delivery" as const,
              stripeCustomerId: null,
              tierId: null,
              addressId: null,
              permissions: {},
            },
            cart_items: [],
            printed: false,
            discarded: false,
            purchased: false,
            notes: "",
            created: new Date(),
            updated: new Date(),
            isServerCart: false,
          };

          // Add to local store
          addLocalCart(newCart);

          // Set as active cart
          setActiveCart(quoteNumber);

          return quoteNumber;
        }
      } catch (error) {
        console.error("[CartProvider] Error adding cart:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [session, addLocalCart, setActiveCart, serverActions]
  );

  // Close a cart tab (hide it if it has items, delete empty server carts from database)
  const closeCartTab = useCallback(
    async (quoteNumber: string): Promise<void> => {
      try {
        // Find the cart to check if it's a server cart
        const cart = carts.find((c) => c.quote_number === quoteNumber);

        // Before doing anything else, ensure the local storage is cleaned up for this cart
        // This ensures we don't get circular reference errors
        if (activeQuoteNumber === quoteNumber) {
          try {
            localStorage.removeItem("active-cart-data");
          } catch (e) {
            console.error("[CartProvider] Error cleaning localStorage:", e);
          }
        }

        if (cart?.isServerCart) {
          // Check if the server cart is empty (has no items)
          const hasItems = cart.cart_items && cart.cart_items.length > 0;

          if (!hasItems) {
            try {
              await serverActions.closeCart(quoteNumber);
            } catch (error) {
              console.error(
                `[CartProvider] Error deleting empty server cart:`,
                error
              );
              // Fall back to hiding the cart if deletion fails
            }
          }

          // Remove from local state (either after deletion or for hiding)
          setServerCarts((prev) =>
            prev.filter((c) => c.quote_number !== quoteNumber)
          );

          // Also remove from Zustand store so it doesn't reappear in UI
          removeLocalCart(quoteNumber);
        } else {
          // For local carts, we remove it completely
          removeLocalCart(quoteNumber);
        }

        // If we're closing the active cart, we need to set a new active cart
        if (activeQuoteNumber === quoteNumber) {
          // Get remaining carts after removal
          const remainingCarts = carts.filter(
            (c) => c.quote_number !== quoteNumber
          );

          if (remainingCarts.length > 0) {
            // Set the first remaining cart as active
            const newActiveCart = remainingCarts[0];
            setActiveCart(newActiveCart.quote_number);
          }
        }
      } catch (error) {
        console.error("[CartProvider] Error closing cart tab:", error);
        throw error;
      }
    },
    [carts, activeQuoteNumber, removeLocalCart, serverActions, setActiveCart]
  );

  // Rename a cart
  const renameCart = useCallback(
    async (quoteNumber: string, name: string): Promise<boolean> => {
      try {
        // Find the cart to check if it's a server cart
        const cart = carts.find((c) => c.quote_number === quoteNumber);

        if (cart?.isServerCart) {
          // For server carts, update on server (works for both authenticated and guest users)
          try {
            // Use the server action to update name
            const updatedCart = await serverActions.updateCartName(
              quoteNumber,
              name
            );

            if (updatedCart) {
              // Update server carts
              setServerCarts((prev) =>
                prev.map((c) =>
                  c.quote_number === quoteNumber ? { ...c, name } : c
                )
              );

              // Also update the Zustand store to keep it in sync
              updateLocalCart(quoteNumber, { name });

              // If this is the active cart, update active-cart-data
              if (activeQuoteNumber === quoteNumber) {
                try {
                  // Create a clean version of the updated cart without circular references
                  const cleanUpdatedCart = {
                    ...updatedCart,
                    isServerCart: true,
                    // Ensure cart_items are properly cleaned of any circular references
                    cart_items: Array.isArray(updatedCart.cart_items)
                      ? updatedCart.cart_items.map((item) => {
                          // Remove any circular reference properties that might have been added
                          const cleanItem = { ...item };
                          if ("_group" in cleanItem) {
                            delete (cleanItem as CartItemWithGroup)._group;
                          }
                          if ("_quoteNumber" in cleanItem) {
                            delete (cleanItem as CartItemWithGroup)
                              ._quoteNumber;
                          }
                          return cleanItem;
                        })
                      : [],
                  };

                  localStorage.setItem(
                    "active-cart-data",
                    JSON.stringify(prepareCartForStorage(cleanUpdatedCart))
                  );
                } catch (error) {
                  console.error(
                    "[CartProvider] Failed to update active-cart-data:",
                    error
                  );
                }
              }
              return true;
            } else {
              console.error(
                `[CartProvider] Server action returned null/undefined cart`
              );
            }
          } catch (error) {
            console.error("[CartProvider] Error updating cart name:", error);
            return false;
          }
        } else {
          // For local carts, update in local store
          updateLocalCart(quoteNumber, { name });
          return true;
        }
        return false;
      } catch (error) {
        console.error("[CartProvider] Error renaming cart:", error);
        return false;
      }
    },
    [carts, activeQuoteNumber, updateLocalCart, serverActions]
  );

  // Clear all items from a cart
  const clearCart = useCallback(
    async (quoteNumber: string): Promise<boolean> => {
      try {
        // Find the cart to check if it's a server cart
        const cart = carts.find((c) => c.quote_number === quoteNumber);

        if (cart?.isServerCart) {
          // For server carts, clear on server (works for both authenticated and guest users)
          try {
            // Use the server action to clear the cart
            await serverActions.clearCart(quoteNumber);

            // Update server carts state
            setServerCarts((prev) =>
              prev.map((c) =>
                c.quote_number === quoteNumber ? { ...c, cart_items: [] } : c
              )
            );

            // Also update the Zustand store so localStorage gets updated
            updateLocalCart(quoteNumber, { cart_items: [] });

            // If this is the active cart, update active-cart-data
            if (activeQuoteNumber === quoteNumber) {
              try {
                const updatedCart = { ...cart, cart_items: [] };
                localStorage.setItem(
                  "active-cart-data",
                  JSON.stringify(prepareCartForStorage(updatedCart))
                );
              } catch (error) {
                console.error(
                  "[CartProvider] Failed to update active-cart-data:",
                  error
                );
              }
            }

            return true;
          } catch (error) {
            console.error("[CartProvider] Error clearing server cart:", error);
            return false;
          }
        } else {
          // For local carts, clear in local store
          updateLocalCart(quoteNumber, { cart_items: [] });
          return true;
        }
      } catch (error) {
        console.error("[CartProvider] Error clearing cart:", error);
        return false;
      }
    },
    [carts, activeQuoteNumber, updateLocalCart, serverActions]
  );

  // Delete a cart completely
  const deleteCartTab = useCallback(
    async (quoteNumber: string): Promise<boolean> => {
      try {
        // Find the cart to check if it's a server cart
        const cart = carts.find((c) => c.quote_number === quoteNumber);

        if (cart?.isServerCart && session?.user) {
          // For server carts, delete on server using server action
          try {
            await serverActions.closeCart(quoteNumber);

            // Remove from server carts
            setServerCarts((prev) =>
              prev.filter((c) => c.quote_number !== quoteNumber)
            );

            return true;
          } catch (error) {
            console.error("[CartProvider] Error deleting server cart:", error);
            return false;
          }
        } else {
          // For local carts, remove from local store
          removeLocalCart(quoteNumber);
          return true;
        }
      } catch (error) {
        console.error("[CartProvider] Error deleting cart:", error);
        return false;
      }
    },
    [carts, session, removeLocalCart, serverActions]
  );

  // Get a cart (retrieve a hidden server cart or verify a local cart exists)
  const retrieveCart = useCallback(
    async (
      quoteNumber: string
    ): Promise<{ success: boolean; cart?: Cart; error?: string }> => {
      try {
        // Check if cart already exists locally
        const existingCart = carts.find((c) => c.quote_number === quoteNumber);
        if (existingCart) {
          // For server carts, always fetch fresh data to ensure synchronization
          if (existingCart.isServerCart && session?.user) {
            try {
              const serverCart = await serverActions.getCart(quoteNumber);

              if (serverCart) {
                const cartWithFlag = {
                  ...serverCart,
                  isServerCart: true,
                };

                // Update server carts with fresh data
                setServerCarts((prev) =>
                  prev.map((cart) =>
                    cart.quote_number === quoteNumber ? cartWithFlag : cart
                  )
                );

                // Also update the Zustand store
                updateLocalCart(quoteNumber, cartWithFlag);

                return { success: true, cart: cartWithFlag };
              }
            } catch (error) {
              console.error(
                "[CartProvider] Error fetching fresh server cart data:",
                error
              );
              // Fall back to existing cart
              return { success: true, cart: existingCart };
            }
          }

          return { success: true, cart: existingCart };
        }

        if (session?.user) {
          // For authenticated users, try to get from server
          try {
            // Use server action to get cart
            const serverCart = await serverActions.getCart(quoteNumber);

            if (serverCart) {
              const cartWithFlag = {
                ...serverCart,
                isServerCart: true,
              };

              // Add to server carts
              setServerCarts((prev) => [...prev, cartWithFlag]);

              // Also add to Zustand store
              addLocalCart(cartWithFlag);

              // Set as active cart
              setActiveCart(cartWithFlag.quote_number);

              return { success: true, cart: cartWithFlag };
            }
          } catch (error) {
            console.error(
              "[CartProvider] Error retrieving server cart:",
              error
            );
            return { success: false, error: "Cart not found on server" };
          }
        }

        // For guest users or if server cart was not found
        return { success: false, error: "Cart not found" };
      } catch (error) {
        console.error("[CartProvider] Error retrieving cart:", error);
        return { success: false, error: String(error) };
      }
    },
    [
      carts,
      session,
      setActiveCart,
      serverActions,
      updateLocalCart,
      addLocalCart,
    ]
  );

  // Bulk cart operation (add or remove multiple items)
  const bulkCartOperation = useCallback(
    async (
      quoteNumber: string,
      operations: { item: CartItem; quantity: number }[]
    ): Promise<{
      success: boolean;
      itemsAdded: number;
      itemsRemoved: number;
    }> => {
      try {
        const cart = carts.find((c) => c.quote_number === quoteNumber);
        if (!cart) {
          throw new Error(`Cart not found: ${quoteNumber}`);
        }

        // Verify operations are clean of circular references
        operations.forEach((op, index) => {
          // Make a deep check for circular references
          try {
            JSON.stringify(op.item);
          } catch (error) {
            console.error(
              `[CartProvider] Circular reference detected in operation ${index}:`,
              error
            );

            // Clean the item
            if ((op.item as CartItemWithGroup)._group) {
              delete (op.item as CartItemWithGroup)._group;
            }

            if ((op.item as CartItemWithGroup)._quoteNumber) {
              delete (op.item as CartItemWithGroup)._quoteNumber;
            }

            // Try again
            try {
              JSON.stringify(op.item);
            } catch (err) {
              console.error(
                `[CartProvider] Failed to clean operation ${index}:`,
                err
              );
            }
          }
        });

        if (cart.isServerCart) {
          // For server carts, use server action (works for both authenticated and guest users)
          try {
            const result = await serverActions.bulkCartOperation(
              quoteNumber,
              operations
            );

            if (result.success) {
              // Re-fetch the cart to get the updated items
              try {
                const updatedCart = await serverActions.getCart(quoteNumber);

                if (updatedCart) {
                  let finalUpdatedCart = {
                    ...updatedCart,
                    isServerCart: true,
                  };

                  // Check if pricing needs to be updated after bulk operation
                  // This is important when items are added with different tier_ids than the user's current tier
                  if (
                    session?.user &&
                    updatedCart.cart_items &&
                    updatedCart.cart_items.length > 0
                  ) {
                    const cartUserData = updatedCart.user_data as UserSession;
                    const cartItems = updatedCart.cart_items as CartItem[];

                    // Check if any items have mismatched tier_ids or if cart user tier differs from current user tier
                    // For company users, always check pricing as their tier might not be in the session
                    const needsPriceUpdate =
                      session.user.companyId || // Always check for company users
                      cartUserData?.tierId !== session.user.tierId ||
                      cartItems.some(
                        (item) => item.tier_id !== session.user.tierId
                      );

                    if (needsPriceUpdate) {
                      try {
                        const pricingResult =
                          await serverActions.updateCartPrices(quoteNumber);
                        if (pricingResult.pricesChanged) {
                          // Re-fetch the cart again to get the updated prices
                          const cartWithUpdatedPrices =
                            await serverActions.getCart(quoteNumber);
                          if (cartWithUpdatedPrices) {
                            finalUpdatedCart = {
                              ...cartWithUpdatedPrices,
                              isServerCart: true,
                            };
                          }
                        }
                      } catch (pricingError) {
                        console.error(
                          `[CartProvider] Error updating pricing after bulk operation:`,
                          pricingError
                        );
                        // Continue with the original cart if pricing update fails
                      }
                    }
                  }

                  // Update server carts state
                  setServerCarts((prev) =>
                    prev.map((c) =>
                      c.quote_number === quoteNumber ? finalUpdatedCart : c
                    )
                  );

                  // Also update the Zustand store so localStorage gets updated
                  updateLocalCart(quoteNumber, finalUpdatedCart);

                  // If this is the active cart, update active-cart-data
                  if (activeQuoteNumber === quoteNumber) {
                    try {
                      localStorage.setItem(
                        "active-cart-data",
                        JSON.stringify(prepareCartForStorage(finalUpdatedCart))
                      );
                    } catch (error) {
                      console.error(
                        "[CartProvider] Failed to update active-cart-data:",
                        error
                      );
                    }
                  }
                }
              } catch (fetchError) {
                console.error(
                  "[CartProvider] Error fetching updated cart:",
                  fetchError
                );
              }

              return {
                success: true,
                itemsAdded: result.itemsAdded,
                itemsRemoved: result.itemsRemoved,
              };
            }

            return {
              success: false,
              itemsAdded: 0,
              itemsRemoved: 0,
            };
          } catch (error) {
            console.error(
              "[CartProvider] Error in server bulk operation:",
              error
            );
            return { success: false, itemsAdded: 0, itemsRemoved: 0 };
          }
        } else {
          // For local carts, update in local store
          const cartItems = Array.isArray(cart.cart_items)
            ? [...cart.cart_items]
            : [];
          let itemsAdded = 0;
          let itemsRemoved = 0;

          // Process each operation
          for (const op of operations) {
            const { item, quantity } = op;

            if (quantity > 0) {
              // Add items
              for (let i = 0; i < quantity; i++) {
                const newItem = {
                  ...item,
                  item_id: generateCartItemId(quoteNumber, item.part_number),
                  addedAt: Date.now(), // Add timestamp for insertion order
                };

                // Process attachments if they exist
                if (item.attachments && item.attachments.length > 0) {
                  newItem.attachments = item.attachments.map((attachment) => ({
                    ...attachment,
                    item_id: generateCartItemId(
                      quoteNumber,
                      attachment.part_number
                    ),
                    addedAt: Date.now(), // Also add timestamp to attachments
                  }));
                }

                cartItems.push(newItem);
                itemsAdded++;
              }
            } else if (quantity < 0) {
              // Remove items (quantity is negative)
              const absQuantity = Math.abs(quantity);

              // Find matching items to remove
              let matchingItems: CartItem[] = [];

              if (item.part_number && item.decal_number) {
                // If both part_number and decal_number are provided, match by both
                matchingItems = cartItems.filter(
                  (i) =>
                    i.part_number === item.part_number &&
                    i.decal_number === item.decal_number
                );
              } else if (item.part_number) {
                // If only part_number is provided, match by part_number
                matchingItems = cartItems.filter(
                  (i) => i.part_number === item.part_number
                );
              } else {
                // Otherwise match by product_id and decal_number
                matchingItems = cartItems.filter((i) => {
                  if (item.decal_number) {
                    return (
                      i.product_id === item.product_id &&
                      i.decal_number === item.decal_number
                    );
                  }
                  return i.product_id === item.product_id && !i.decal_number;
                });
              }

              // Sort matching items by addedAt (oldest first)
              matchingItems.sort((a, b) => (a.addedAt || 0) - (b.addedAt || 0));

              // Determine how many items to actually remove
              const itemsToRemove = Math.min(absQuantity, matchingItems.length);

              if (itemsToRemove > 0) {
                // Get the IDs of the items we want to remove (oldest first)
                const itemIdsToRemove = matchingItems
                  .slice(0, itemsToRemove)
                  .map((i) => i.item_id);

                // Remove the items by filtering them out
                const newCartItems = cartItems.filter(
                  (i) => !itemIdsToRemove.includes(i.item_id || "")
                );

                itemsRemoved += itemsToRemove;

                // Replace cart items with filtered list
                cartItems.length = 0;
                cartItems.push(...newCartItems);
              }
            }
          }

          // Before updating, ensure there are no circular references
          const cleanCartItems = cartItems.map((item) => {
            // Create a clean item without problematic properties
            const safeItem: CartItem = {
              item_id: item.item_id,
              product_id: item.product_id,
              part_number: item.part_number,
              decal_number: item.decal_number,
              price: item.price,
              title: item.title,
              image: item.image,
              tier_id: item.tier_id,
              weight: item.weight || 0,
              moq: item.moq || 1,
              shipping_weight: item.shipping_weight || 0,
              shipping_width: item.shipping_width,
              shipping_height: item.shipping_height,
              shipping_length: item.shipping_length,
              width: item.width,
              height: item.height,
              length: item.length,
            };

            // Handle attachments if present
            if (item.attachments && Array.isArray(item.attachments)) {
              safeItem.attachments = item.attachments.map((att) => ({
                item_id: att.item_id,
                product_id: att.product_id,
                part_number: att.part_number,
                decal_number: att.decal_number,
                price: att.price || 0,
                title: att.title || "",
                image: att.image,
                tier_id: att.tier_id,
                quantity: att.quantity || 1,
                shipping_weight: att.shipping_weight || 0,
                shipping_width: att.shipping_width,
                shipping_height: att.shipping_height,
                shipping_length: att.shipping_length,
                weight: att.weight || 0,
                width: att.width,
                height: att.height,
                length: att.length,
                moq: att.moq || 1,
              }));
            }

            return safeItem;
          });

          // Update local cart with cleaned items
          updateLocalCart(quoteNumber, { cart_items: cleanCartItems });

          // Check if this is a local cart that became empty and update needsMigration
          if (!cart.isServerCart && cleanCartItems.length === 0) {
            // Update the store to set needsMigration to false
            const store = useCartStore.getState();
            store.updateCart(quoteNumber, {
              needsMigration: false,
            });

            // Also update localStorage
            try {
              if (activeQuoteNumber === quoteNumber) {
                const cartData = localStorage.getItem("active-cart-data");
                if (cartData) {
                  const parsedCart = JSON.parse(cartData);
                  const updatedCart = {
                    ...parsedCart,
                    cart_items: [],
                    needsMigration: false,
                  };
                  localStorage.setItem(
                    "active-cart-data",
                    JSON.stringify(prepareCartForStorage(updatedCart))
                  );
                }
              }
            } catch (storageError) {
              console.error(
                "[CartProvider] Failed to update localStorage:",
                storageError
              );
            }
          }

          return {
            success: true,
            itemsAdded,
            itemsRemoved,
          };
        }
      } catch (error) {
        console.error("[CartProvider] Error in bulk cart operation:", error);
        return {
          success: false,
          itemsAdded: 0,
          itemsRemoved: 0,
        };
      }
    },
    [carts, activeQuoteNumber, updateLocalCart, serverActions, session]
  );

  // Convert guest cart to server cart
  const convertGuestCartToServer = useCallback(
    async (quoteNumber: string): Promise<Cart | null> => {
      try {
        const cart = carts.find((c) => c.quote_number === quoteNumber);
        if (!cart) {
          throw new Error(`Cart not found: ${quoteNumber}`);
        }

        if (cart.isServerCart) {
          return cart; // Already a server cart
        }

        // Create default user session for guest users
        const defaultUserData: UserSession = {
          role: "user" as const,
          id: null,
          email: null,
          firstName: null,
          lastName: null,
          emailVerified: null,
          jobTitle: null,
          phone: null,
          image: null,
          companyId: null,
          companyName: null,
          companyImage: null,
          paymentTerms: "cash_on_delivery" as const,
          stripeCustomerId: null,
          tierId: "460a3d62-c14a-4b55-8045-7ec079527d47", // Default tier ID for guest users
          addressId: null,
          permissions: {},
        };

        // Clean the cart data to avoid circular references
        const cleanCart = prepareCartForStorage(cart);

        // Clean cart items using the new function and ensure correct tier_id
        const cleanedCartItems = cleanCartItemsForServer(
          cart.cart_items as CartItem[]
        ).map((item) => ({
          ...item,
          tier_id: item.tier_id || defaultUserData.tierId!,
        }));

        try {
          // Instead of using convertGuestCartToUserCart, use createCart directly
          // This avoids the complex schema validation issues
          const newServerCart = await serverActions.createCart(
            cleanCart.name || "Guest Cart",
            cleanedCartItems,
            true // Set printed to true since this is for quote download
          );

          if (newServerCart) {
            const serverCartWithFlag = {
              ...newServerCart,
              isServerCart: true,
              // Preserve needsMigration for guest users (no authenticated session)
              // Only clear needsMigration if user is logged in
              needsMigration: session?.user ? false : true,
            };

            // Add to server carts
            setServerCarts((prev) => [...prev, serverCartWithFlag]);

            // Use replaceCart to maintain the tab position
            // This replaces the local cart with the server cart in the same position
            // without removing and adding, preserving the tab order
            replaceLocalCart(quoteNumber, serverCartWithFlag);

            // Set the new server cart as active
            setActiveCart(serverCartWithFlag.quote_number);

            // Update localStorage with the new server cart
            try {
              localStorage.setItem(
                "active-cart-data",
                JSON.stringify(prepareCartForStorage(serverCartWithFlag))
              );
            } catch (error) {
              console.error(
                "[CartProvider] Failed to update localStorage:",
                error
              );
            }

            return serverCartWithFlag;
          } else {
            console.error(
              "[CartProvider] Server action returned null/undefined cart"
            );
          }
        } catch (error) {
          console.error("[CartProvider] Error creating server cart:", error);
        }

        return null;
      } catch (error) {
        console.error("[CartProvider] Error converting guest cart:", error);
        return null;
      }
    },
    [
      carts,
      setActiveCart,
      setServerCarts,
      replaceLocalCart,
      serverActions,
      session,
    ]
  );

  // Shared cart initialization function
  const initializeUserCartsOnLogin = useCallback(async () => {
    try {
      // First check what we have in the Zustand store
      const currentStore = useCartStore.getState();
      const existingStoreCarts = currentStore.carts;
      const serverCartsInStore = existingStoreCarts.filter(
        (cart) => cart.isServerCart
      );

      if (serverCartsInStore.length > 0) {
        // Just update the server carts state from what's in the store
        setServerCarts(serverCartsInStore);

        // Ensure we have an active cart
        if (!currentStore.activeQuoteNumber && serverCartsInStore.length > 0) {
          const firstCart = serverCartsInStore[0];
          currentStore.setActiveCart(firstCart.quote_number);

          // Update active-cart-data in localStorage
          try {
            localStorage.setItem(
              "active-cart-data",
              JSON.stringify(prepareCartForStorage(firstCart))
            );
          } catch (error) {
            console.error(
              "[CartProvider] Failed to set active-cart-data:",
              error
            );
          }
        }
        return;
      }

      // IMPROVED LOGIC: Only skip if there are local carts with items that need migration
      // Empty local carts don't prevent fetching the most recent cart from the database
      const localCartsWithItems = existingStoreCarts.filter(
        (cart) =>
          !cart.isServerCart && cart.cart_items && cart.cart_items.length > 0
      );

      if (localCartsWithItems.length > 0) {
        // Don't fetch most recent cart if there are local carts with items that need migration
        return;
      }

      // At this point, we either have no local carts, or only empty local carts
      // Clear any empty local carts before fetching from server
      const emptyLocalCarts = existingStoreCarts.filter(
        (cart) =>
          !cart.isServerCart &&
          (!cart.cart_items || cart.cart_items.length === 0)
      );

      if (emptyLocalCarts.length > 0) {
        emptyLocalCarts.forEach((cart) => {
          currentStore.removeCart(cart.quote_number);
        });
      }

      const mostRecentCart = await serverActions.getMostRecentCart();

      if (mostRecentCart && !mostRecentCart.purchased) {
        // Add the most recent cart to local state
        const serverCartWithFlag = {
          ...mostRecentCart,
          isServerCart: true,
        };

        setServerCarts([serverCartWithFlag]);

        // Clear local store and add the most recent cart (authenticated users should start clean)
        currentStore.clearAllCarts();
        currentStore.addCart(serverCartWithFlag);

        // Set it as active
        currentStore.setActiveCart(serverCartWithFlag.quote_number);

        // Update active-cart-data in localStorage
        try {
          localStorage.setItem(
            "active-cart-data",
            JSON.stringify(prepareCartForStorage(serverCartWithFlag))
          );
        } catch (error) {
          console.error(
            "[CartProvider] Failed to set active-cart-data:",
            error
          );
        }

        toast({
          title: "Welcome back!",
          description: `We have loaded your previous cart with ${mostRecentCart.cart_items.length} item${mostRecentCart.cart_items.length === 1 ? "" : "s"}.`,
          variant: "success",
        });
      } else {
        // Clear any existing local carts before creating new server cart
        currentStore.clearAllCarts();

        // Create a new empty server cart
        await addCart("Cart");

        toast({
          title: "Welcome back!",
          description: `We see you've logged in, but you don't have any carts yet, so we created a new one for you.`,
          variant: "success",
        });
      }
    } catch (error) {
      console.error("[CartProvider] Error during cart initialization:", error);
      // Reset flag on error so initialization can be retried
      cartInitializationRef.current = false;
    }
  }, [serverActions, setServerCarts, addCart, toast]);

  // Effect to manage loading and data source ready states
  useEffect(() => {
    // If we have the zustand store initialized or have carts, we're ready
    if (initialized || carts.length > 0) {
      setIsDataSourceReady(true);
      setIsLoading(false);
    } else if (status !== "loading") {
      // If auth status is not loading and we still don't have carts, we're still ready
      setIsDataSourceReady(true);
      setIsLoading(false);
    }
  }, [initialized, carts.length, status]);

  // Effect to handle fallback cart initialization for authenticated users
  useEffect(() => {
    if (
      status === "authenticated" &&
      session?.user &&
      migrationCompleted &&
      !migrationInProgress &&
      isDataSourceReady &&
      !isLoading
    ) {
      const timeoutId = setTimeout(() => {
        // Check the Zustand store directly, not the combined carts
        const currentStore = useCartStore.getState();

        // Check if we have any server carts or local carts that need migration
        const serverCarts = currentStore.carts.filter(
          (cart) => cart.isServerCart
        );
        const localCartsWithItems = currentStore.carts.filter(
          (cart) =>
            !cart.isServerCart && cart.cart_items && cart.cart_items.length > 0
        );

        const shouldInitialize =
          serverCarts.length === 0 &&
          localCartsWithItems.length === 0 &&
          !cartInitializationRef.current;

        if (shouldInitialize) {
          cartInitializationRef.current = true; // Prevent duplicate initialization

          // Reuse the same initialization logic
          initializeUserCartsOnLogin();
        }
      }, 1000); // 1 second delay to ensure migration has completed

      // Cleanup function
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [
    status,
    session,
    migrationCompleted,
    migrationInProgress,
    isDataSourceReady,
    isLoading,
    addCart,
    serverActions,
    setServerCarts,
    initializeUserCartsOnLogin,
  ]);

  // Effect to track login status changes and listen for migration triggers
  useEffect(() => {
    const currentStatus = status;

    // Handle user login
    if (
      currentStatus === "authenticated" &&
      lastSessionStatus !== "authenticated" &&
      session?.user &&
      !loginProcessingRef.current // Prevent multiple simultaneous login processing
    ) {
      // Mark login as being processed
      loginProcessingRef.current = true;

      // Reset flags on fresh login to ensure clean state
      if (migrationInitiatedRef.current || migrationInProgress) {
        migrationInitiatedRef.current = false;
        setMigrationInProgress(false);
        setMigrationCompleted(false);
      }

      // Clear any existing conversion tracking
      cartsBeingConvertedRef.current.clear();

      // Longer delay to ensure all effects settle and prevent race conditions
      setTimeout(() => {
        // Check if we're still in the same login session
        if (!loginProcessingRef.current) {
          return;
        }

        // Just check if we need to initialize user carts (no local carts to convert)
        const store = useCartStore.getState();

        // Check if we have any server carts or local carts that need migration
        const serverCarts = store.carts.filter((cart) => cart.isServerCart);
        const localCartsWithItems = store.carts.filter(
          (cart) =>
            !cart.isServerCart && cart.cart_items && cart.cart_items.length > 0
        );

        // Only initialize if:
        // 1. No server carts exist, AND
        // 2. No local carts with items that need migration exist, AND
        // 3. Initialization hasn't already been attempted
        const shouldInitialize =
          serverCarts.length === 0 &&
          localCartsWithItems.length === 0 &&
          !cartInitializationRef.current;

        if (shouldInitialize) {
          cartInitializationRef.current = true; // Prevent duplicate initialization

          // Initialize carts immediately
          initializeUserCartsOnLogin().finally(() => {
            loginProcessingRef.current = false; // Reset login processing flag
          });
        } else {
          loginProcessingRef.current = false; // Reset login processing flag
        }

        // Set migration as completed to prevent any other migration triggers
        setMigrationCompleted(true);
      }, 500); // Increased delay to prevent race conditions
    }

    setLastSessionStatus(currentStatus);
  }, [
    status,
    lastSessionStatus,
    session,
    migrationInProgress,
    migrationCompleted,
    serverActions,
    addCart,
    initializeUserCartsOnLogin,
  ]);

  // Reset migration status when user logs out
  useEffect(() => {
    if (status === "unauthenticated") {
      setMigrationCompleted(false);
      setMigrationInProgress(false);
      setLastSessionStatus(null);
      migrationInitiatedRef.current = false; // Reset the ref on logout
      cartInitializationRef.current = false; // Reset cart initialization flag on logout
      loginProcessingRef.current = false; // Reset login processing flag on logout
      cartsBeingConvertedRef.current.clear(); // Clear any pending conversions on logout

      // Clear all conversion tracking from session storage
      if (typeof window !== "undefined") {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (
            key &&
            (key.startsWith("cart-conversion-") ||
              key.startsWith("cart-creation-lock-"))
          ) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));
      }
    }
  }, [status]);

  // Add Pusher subscription for real-time cart synchronization across devices
  useEffect(() => {
    if (!session?.user || !activeQuoteNumber) return;

    // Find the active cart to check if it's a server cart
    const activeCart = carts.find(
      (cart) => cart.quote_number === activeQuoteNumber
    );
    if (!activeCart?.isServerCart) return;

    const cartChannel = pusherClient.subscribe(`cart-${activeQuoteNumber}`);

    // Listen for cart updates from other devices
    cartChannel.bind(
      "cart-updated",
      async (data: {
        type: string;
        itemsAdded?: number;
        itemsRemoved?: number;
        totalItems?: number;
        quoteNumber?: string;
      }) => {
        // Only process updates for the current active cart
        if (data.quoteNumber && data.quoteNumber !== activeQuoteNumber) {
          return;
        }

        // Fetch fresh cart data from server to sync across devices
        try {
          const freshCart = await serverActions.getCart(activeQuoteNumber);

          if (freshCart) {
            const cartWithFlag = {
              ...freshCart,
              isServerCart: true,
            };

            // Update server carts state
            setServerCarts((prev) =>
              prev.map((cart) =>
                cart.quote_number === activeQuoteNumber ? cartWithFlag : cart
              )
            );

            // Update Zustand store
            updateLocalCart(activeQuoteNumber, cartWithFlag);
          }
        } catch (error) {
          console.error(`[CartProvider] Error synchronizing cart data:`, error);
        }
      }
    );

    // Listen for cart deletion
    cartChannel.bind("cart-deleted", (data: { quoteNumber: string }) => {
      if (data.quoteNumber === activeQuoteNumber) {
        // Remove from server carts
        setServerCarts((prev) =>
          prev.filter((cart) => cart.quote_number !== data.quoteNumber)
        );

        // Remove from Zustand store
        removeLocalCart(data.quoteNumber);
      }
    });

    // Cleanup subscription when effect re-runs or component unmounts
    return () => {
      cartChannel.unbind_all();
      pusherClient.unsubscribe(`cart-${activeQuoteNumber}`);
    };
  }, [
    session,
    activeQuoteNumber,
    carts,
    serverActions,
    updateLocalCart,
    removeLocalCart,
  ]);

  // Function to sync all server carts
  const syncAllServerCarts = useCallback(async () => {
    if (!session?.user || migrationInProgress) return;

    try {
      // Get the current carts from Zustand store
      const currentStore = useCartStore.getState();
      const serverCartsInStore = currentStore.carts.filter(
        (cart) => cart.isServerCart
      );

      if (serverCartsInStore.length === 0) {
        return;
      }

      // Sync each server cart individually
      const syncPromises = serverCartsInStore.map(async (cart) => {
        try {
          const freshCart = await serverActions.getCart(cart.quote_number);
          if (freshCart && !freshCart.purchased) {
            return {
              ...freshCart,
              isServerCart: true,
            };
          }
          // If cart is purchased, return null to filter it out
          return null;
        } catch (error) {
          console.error(
            `[CartProvider] Error syncing cart ${cart.quote_number}:`,
            error
          );
          // Return the existing cart if sync fails
          return cart;
        }
      });

      const syncedCarts = await Promise.all(syncPromises);

      // Filter out null values (purchased carts) and update state
      const validCarts = syncedCarts.filter((cart) => cart !== null) as Cart[];

      if (validCarts.length > 0) {
        setServerCarts(validCarts);

        // Update each cart in Zustand store
        validCarts.forEach((cart) => {
          updateLocalCart(cart.quote_number, cart);
        });

        // Update localStorage if active cart was updated
        const activeCartData = validCarts.find(
          (cart) => cart.quote_number === activeQuoteNumber
        );

        if (activeCartData) {
          try {
            localStorage.setItem(
              "active-cart-data",
              JSON.stringify(prepareCartForStorage(activeCartData))
            );
          } catch (error) {
            console.error(
              "[CartProvider] Failed to update active-cart-data:",
              error
            );
          }
        }
      }

      // Remove any purchased carts from the store
      const purchasedCarts = serverCartsInStore.filter(
        (storeCart) =>
          !validCarts.some(
            (validCart) => validCart.quote_number === storeCart.quote_number
          )
      );

      purchasedCarts.forEach((cart) => {
        removeLocalCart(cart.quote_number);
      });
    } catch (error) {
      console.error("[CartProvider] Error syncing carts from server:", error);
    }

    lastSyncTimeRef.current = Date.now();
  }, [
    session,
    activeQuoteNumber,
    serverActions,
    updateLocalCart,
    removeLocalCart,
    migrationInProgress,
  ]);

  // Effect to handle visibility changes and sync carts
  useEffect(() => {
    const handleVisibilityChange = () => {
      const currentVisibility = document.visibilityState;
      const previousVisibility = visibilityStateRef.current;

      // If page becomes visible after being hidden
      if (previousVisibility === "hidden" && currentVisibility === "visible") {
        const timeSinceLastSync = Date.now() - lastSyncTimeRef.current;

        // Only sync if it's been more than 5 seconds since last sync
        if (timeSinceLastSync > 5000) {
          syncAllServerCarts();
        }
      }

      visibilityStateRef.current = currentVisibility;
    };

    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Sync on mount if we have an authenticated session
    if (session?.user && initialized) {
      syncAllServerCarts();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [session, initialized, syncAllServerCarts]);

  // Effect to sync carts when coming online
  useEffect(() => {
    const handleOnline = () => {
      syncAllServerCarts();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [syncAllServerCarts]);

  // Immediate cart monitoring effect - triggers when carts change
  useEffect(() => {
    if (!session?.user || migrationInProgress || carts.length === 0) return;

    const checkCartsNeedingProcessing = async () => {
      // Don't run during active login processing to prevent race conditions
      if (loginProcessingRef.current) {
        return;
      }

      try {
        const currentStore = useCartStore.getState();

        // Find carts that need immediate processing - check and mark for conversion atomically
        const localCartsWithItems = currentStore.carts.filter((cart) => {
          const needsConversion =
            !cart.isServerCart &&
            cart.cart_items &&
            cart.cart_items.length > 0 &&
            !isCartBeingConverted(cart.quote_number);

          // If this cart needs conversion, immediately mark it as being converted
          if (needsConversion) {
            markCartAsBeingConverted(cart.quote_number);
          }

          return needsConversion;
        });

        const serverCartsNeedingUserUpdate = currentStore.carts.filter(
          (cart) => cart.isServerCart && cart.needsMigration === true
        );

        // If we find carts needing immediate processing, process them
        if (
          localCartsWithItems.length > 0 ||
          serverCartsNeedingUserUpdate.length > 0
        ) {
          // Process local carts first
          for (const localCart of localCartsWithItems) {
            try {
              // Acquire creation lock to prevent concurrent creations
              if (!acquireCreationLock()) {
                clearConversionTracking(localCart.quote_number);
                continue;
              }

              try {
                const newCart = await serverActions.createCart(
                  localCart.name || "Converted Cart",
                  cleanCartItemsForServer(localCart.cart_items as CartItem[])
                );

                if (newCart) {
                  const serverCart = {
                    ...newCart,
                    isServerCart: true,
                  };

                  replaceLocalCart(localCart.quote_number, serverCart);
                }
              } catch (error) {
                console.error(
                  `[CartProvider] Error in immediate conversion:`,
                  error
                );
              } finally {
                // Remove from conversion tracking
                clearConversionTracking(localCart.quote_number);
              }
            } catch (error) {
              console.error(
                `[CartProvider] Error in immediate cart processing:`,
                error
              );
            }
          }

          // Process server carts needing user updates
          for (const serverCart of serverCartsNeedingUserUpdate) {
            try {
              const result = await serverActions.updateCartUserData(
                serverCart.quote_number,
                session.user
              );

              if (result.success) {
                updateLocalCart(serverCart.quote_number, {
                  user_data: session.user,
                  needsMigration: false,
                });
              }
            } catch (error) {
              console.error(
                `[CartProvider] Error in immediate server cart update:`,
                error
              );
            }
          }
        }
      } catch (error) {
        console.error(
          "[CartProvider] Error in immediate cart processing:",
          error
        );
      }
    };

    // Reduce delay for faster immediate processing (was 500ms)
    const timeoutId = setTimeout(checkCartsNeedingProcessing, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    carts, // Triggers when carts array changes
    session?.user?.id,
    session?.user?.tierId,
    migrationInProgress,
    serverActions,
    replaceLocalCart,
    updateLocalCart,
    acquireCreationLock,
    releaseCreationLock,
    clearConversionTracking,
    markCartAsBeingConverted,
    isCartBeingConverted,
    session?.user,
  ]);

  return (
    <CartContext.Provider
      value={{
        carts,
        activeQuoteNumber,
        isLoading,
        isDataSourceReady,
        hasAvailableCarts: carts.length > 0,
        setActiveCart,
        addCart,
        closeCartTab,
        renameCart,
        clearCart,
        deleteCartTab,
        getCart: retrieveCart,
        bulkCartOperation,
        convertGuestCartToServer,
        updateCartsOrder: updateLocalCartsOrder,
        replaceCart: replaceLocalCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export { useCart };
